import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { useTranslation } from '@frontend/i18n';
import {
  CheckboxField,
  DropdownField,
  FormRow,
  MoneyField,
  MultiselectField,
  NumberField,
  TextareaField,
  TextField,
  UseFormReturnType,
} from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { appointmentTypePrimaryColorPalette } from '../AppointmentTypesV3/appointment-type-color-picker/appointment-type-color-palette';
import { ColorPickerModal } from '../ColorPicker/ColorPickerModal';
import { ServiceTypeVariant } from '.';

type ServiceTypeFormConfig = {
  color: { type: 'text'; value: string };
  appointmentType: { type: 'dropdown'; value: string };
  source: { type: 'dropdown'; value: string };
  serviceName: { type: 'text'; value: string };
  duration: { type: 'number'; value: string };
  procedureCodes: { type: 'checklist'; value: string[] };
  serviceDescription: { type: 'text'; value: string };
  shouldCollectPayment: { type: 'checkbox'; value: boolean };
  prePaymentAmount: { type: 'money'; value: string };
};

type Props = {
  variant: ServiceTypeVariant;
  appointmentTypes: AppointmentType[];
  sources: DataSource[];
  procedureCodes: { id: string; label: string }[];
  getFieldProps: UseFormReturnType<ServiceTypeFormConfig, keyof ServiceTypeFormConfig>['getFieldProps'];
};

export const ServiceTypeForm = ({ variant, appointmentTypes, sources, procedureCodes, getFieldProps }: Props) => {
  const { t } = useTranslation('schedule-settings');

  if (variant === 'core') {
    return (
      <>
        <FormRow cols={[10, 45, 45]}>
          <AppointmentColorPicker getFieldProps={getFieldProps} />
          <TextField label={t('Service Name')} {...getFieldProps('serviceName')} />
          <NumberField label={t('Duration (Min)')} {...getFieldProps('duration')} />
        </FormRow>
        <CommonFormFields getFieldProps={getFieldProps} />
      </>
    );
  }

  if (variant === 'general') {
    return (
      <>
        <FormRow cols={[10, 90]}>
          <AppointmentColorPicker getFieldProps={getFieldProps} disabled />
          <DropdownField label={t('Source')} {...getFieldProps('source')}>
            {sources?.map((item) => (
              <DropdownField.Option key={item.sourceId} value={item.sourceId ?? ''}>
                {item.sourceName ?? ''}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </FormRow>
        <FormRow cols={[50, 50]}>
          <TextField label={t('Service Name')} {...getFieldProps('serviceName')} />
          <NumberField label={t('Duration (Min)')} {...getFieldProps('duration')} />
        </FormRow>
        <FormRow>
          <MultiselectField label={t('Procedure Codes (Optional)')} {...getFieldProps('procedureCodes')}>
            {procedureCodes?.map((item) => (
              <MultiselectField.Option key={item.id} value={item.label}>
                {item.label}
              </MultiselectField.Option>
            ))}
          </MultiselectField>
        </FormRow>
        <CommonFormFields getFieldProps={getFieldProps} />
      </>
    );
  }

  return (
    <>
      <FormRow cols={[10, 45, 45]}>
        <AppointmentColorPicker getFieldProps={getFieldProps} disabled />
        <DropdownField
          disabled
          css={{ maxWidth: 220 }}
          label={t('Appointment Type')}
          {...getFieldProps('appointmentType')}
        >
          {appointmentTypes?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id ?? ''}>
              {(item.displayName || item.externalName) ?? ''}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <DropdownField disabled css={{ maxWidth: 220 }} label={t('Source')} {...getFieldProps('source')}>
          {sources?.map((item) => (
            <DropdownField.Option key={item.sourceId} value={item.sourceId ?? ''}>
              {item.sourceName ?? ''}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </FormRow>
      <FormRow cols={[50, 50]}>
        <TextField label={t('Service Name')} {...getFieldProps('serviceName')} />
        <NumberField label={t('Duration (Min)')} {...getFieldProps('duration')} />
      </FormRow>
      <FormRow>
        <MultiselectField label={t('Procedure Codes (Optional)')} {...getFieldProps('procedureCodes')}>
          {procedureCodes?.map((item) => (
            <MultiselectField.Option key={item.id} value={item.id}>
              {item.label}
            </MultiselectField.Option>
          ))}
        </MultiselectField>
      </FormRow>
      <CommonFormFields getFieldProps={getFieldProps} />
    </>
  );
};

type CommonFormFieldsProps = {
  getFieldProps: Props['getFieldProps'];
};

const CommonFormFields = ({ getFieldProps }: CommonFormFieldsProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <>
      <FormRow>
        <TextareaField
          label={t('Service Description')}
          {...getFieldProps('serviceDescription')}
          placeholder={t('Describe the service type for patients...')}
        />
      </FormRow>
      <FormRow>
        <CheckboxField
          {...getFieldProps('shouldCollectPayment')}
          label={t('Collect a pre-payment for this service type')}
        />
      </FormRow>
      <FormRow>
        <MoneyField
          disabled={!getFieldProps('shouldCollectPayment').value}
          label={t('Pre-Payment Amount ($)')}
          helperText={t('Whole dollar amounts only')}
          {...getFieldProps('prePaymentAmount')}
        />
      </FormRow>
    </>
  );
};

type AppointmentColorPickerProps = {
  disabled?: boolean;
  getFieldProps: Props['getFieldProps'];
};

const AppointmentColorPicker = ({ disabled, getFieldProps }: AppointmentColorPickerProps) => (
  <div>
    <ColorPickerModal
      selectedColor={getFieldProps('color').value ?? PRIMARY_EVENT_COLOR}
      onChange={(color) => getFieldProps('color').onChange({ name: 'color', value: color })}
      palette={appointmentTypePrimaryColorPalette}
      gridColumns={9}
      disabled={disabled}
    />
  </div>
);
